import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Download, Search, Table2, Loader2, ClipboardList, BookOpen, Database } from 'lucide-react'
import { courseApi, exportApi, type Gradebook } from '../api/lessonBuilderApi'

// Sổ điểm hợp nhất (GV): học viên × hoạt động chấm điểm (đề thi online + quiz bài giảng).
const pctColor = (p: number) =>
  p >= 80 ? 'text-emerald-700 bg-emerald-50' : p >= 50 ? 'text-amber-700 bg-amber-50' : 'text-rose-600 bg-rose-50'

const GradebookPage: React.FC = () => {
  const [data, setData] = useState<Gradebook | null>(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')

  useEffect(() => {
    document.title = 'Sổ điểm hợp nhất | VatLi365'
    courseApi.gradebook().then(setData).finally(() => setLoading(false))
  }, [])

  const students = useMemo(() => {
    const list = data?.students || []
    const k = q.trim().toLowerCase()
    return k ? list.filter((s) => (s.name + ' ' + s.email).toLowerCase().includes(k)) : list
  }, [data, q])

  const exportCsv = () => {
    if (!data) return
    const cols = data.columns
    const head = ['Học viên', 'Email', ...cols.map((c) => `${c.title} (/${c.max})`), 'TB %']
    const esc = (v: any) => {
      const s = String(v ?? '')
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const lines = [head.map(esc).join(',')]
    for (const s of data.students) {
      const row = [s.name, s.email, ...cols.map((c) => {
        const cell = s.scores[c.key]
        return cell ? `${cell.score}` : ''
      }), `${s.avgPercent}`]
      lines.push(row.map(esc).join(','))
    }
    // BOM để Excel mở đúng tiếng Việt
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'so-diem-vatli365.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
          <Link to="/teacher/khoa-hoc" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft size={16} /> Khoá học
          </Link>
          <div className="md:ml-1">
            <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
              <Table2 className="text-blue-600" size={24} /> Sổ điểm hợp nhất
            </h1>
            <p className="text-sm text-slate-500">Điểm của học viên trên đề thi online &amp; quiz trong bài giảng của bạn (lấy điểm cao nhất mỗi hoạt động).</p>
          </div>
          <div className="md:ml-auto flex items-center gap-2">
            <button
              onClick={async () => { try { const n = await exportApi.xapi(); if (!n) alert('Chưa có bản ghi học tập để xuất.') } catch { alert('Xuất xAPI chưa thành công.') } }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
              title="Xuất bản ghi học tập theo chuẩn xAPI (JSON) — nạp vào LRS/phân tích"
            >
              <Database size={16} /> Xuất xAPI
            </button>
            <button
              onClick={exportCsv}
              disabled={!data || data.students.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold shadow hover:bg-blue-700 disabled:opacity-40"
            >
              <Download size={16} /> Xuất CSV
            </button>
          </div>
        </div>

        {/* Thanh tìm kiếm + tóm tắt */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 w-full sm:w-80">
            <Search size={16} className="text-gray-400" />
            <input className="flex-1 bg-transparent outline-none text-sm" placeholder="Tìm học viên / email…"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {data && (
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ClipboardList size={15} className="text-slate-400" /> {data.total_activities} hoạt động</span>
              <span className="inline-flex items-center gap-1.5"><BookOpen size={15} className="text-slate-400" /> {data.total_students} học viên</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gray-300" /></div>
        ) : !data || data.students.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
            Chưa có dữ liệu điểm. Khi học viên làm đề thi online, quiz trong bài giảng hoặc nộp bài tập tự luận, điểm sẽ hiện ở đây.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 font-bold text-slate-700 border-b border-gray-200 min-w-[200px]">Học viên</th>
                  {data.columns.map((c) => (
                    <th key={c.key} className="px-3 py-3 text-center font-bold text-slate-600 border-b border-gray-200 whitespace-nowrap max-w-[160px]">
                      <span className="block truncate" title={c.title}>{c.title}</span>
                      <span className={`block text-[10px] font-medium ${c.type === 'exam' ? 'text-blue-500' : c.type === 'assignment' ? 'text-sky-600' : 'text-fuchsia-500'}`}>
                        {c.type === 'exam' ? 'Đề thi' : c.type === 'assignment' ? 'Bài tập' : 'Quiz'} · /{c.max}
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center font-bold text-slate-700 border-b border-gray-200 bg-slate-100">TB</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={i} className="hover:bg-slate-50/60">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2.5 border-b border-gray-100">
                      <div className="font-semibold text-slate-800 truncate max-w-[220px]">{s.name}</div>
                      {s.email && <div className="text-[11px] text-slate-400 truncate max-w-[220px]">{s.email}</div>}
                    </td>
                    {data.columns.map((c) => {
                      const cell = s.scores[c.key]
                      return (
                        <td key={c.key} className="px-3 py-2.5 text-center border-b border-gray-100">
                          {cell ? (
                            <span className={`inline-block px-2 py-0.5 rounded-md font-bold ${pctColor(cell.percent)}`}>
                              {cell.score}
                            </span>
                          ) : (
                            <span className="text-gray-300">–</span>
                          )}
                        </td>
                      )
                    })}
                    <td className="px-3 py-2.5 text-center border-b border-gray-100 bg-slate-50/60">
                      <span className={`inline-block px-2 py-0.5 rounded-md font-black ${pctColor(s.avgPercent)}`}>{s.avgPercent}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default GradebookPage
